/**
 * Switching the editor between rich text and plain text (Outlook's "Format text: Plain text").
 *
 * Rich → plain walks the editor document (not HTML): lists keep "- " / "1. " markers, quotes get
 * "> ", images become "[name]". The text alternative of sent HTML mail is generated by the server
 * from the final HTML instead (it also covers the quoted original), so the two serve different
 * inputs and are not duplicates.
 */
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { escapeHtml } from '../mail/sanitize';

const QUOTE_SEPARATOR = '_'.repeat(32);

function inlineText(node: ProseMirrorNode): string {
  let out = '';
  node.forEach((child) => {
    if (child.isText) out += child.text ?? '';
    else if (child.type.name === 'hardBreak') out += '\n';
    else if (child.type.name === 'image') out += `[${String(child.attrs.alt ?? 'image')}]`;
    else out += inlineText(child);
  });
  return out;
}

function blockLines(node: ProseMirrorNode): string[] {
  switch (node.type.name) {
    case 'bulletList':
    case 'orderedList': {
      const ordered = node.type.name === 'orderedList';
      let index = Number(node.attrs.start ?? 1);
      const lines: string[] = [];
      node.forEach((item) => {
        const marker = ordered ? `${index++}. ` : '- ';
        const pad = ' '.repeat(marker.length);
        childLines(item).forEach((line, i) => lines.push(i === 0 ? marker + line : pad + line));
      });
      return lines;
    }
    case 'blockquote':
      return childLines(node).map((line) => (line === '' ? '>' : `> ${line}`));
    case 'signature':
      return childLines(node);
    // The quoted history keeps Outlook's plain-text separator line.
    case 'mailQuote':
      return [QUOTE_SEPARATOR, ...childLines(node)];
    default:
      return node.isTextblock ? inlineText(node).split('\n') : childLines(node);
  }
}

function childLines(node: ProseMirrorNode): string[] {
  const lines: string[] = [];
  node.forEach((child) => lines.push(...blockLines(child)));
  return lines;
}

/** The editor document as plain text, one line per paragraph. */
export function documentToPlainText(doc: ProseMirrorNode): string {
  // Without the empty paragraph TipTap keeps after a final list/quote/signature (trailing node).
  return childLines(doc).join('\n').replace(/\n+$/, '');
}

/** Plain text → editor HTML (one paragraph per line, empty lines kept). */
export function plainTextToEditorHtml(text: string): string {
  return text
    .split(/\r?\n/)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join('');
}
